<template>
  <div>
    <div
      v-if="isPostListFetching || isFanFetching"
      class="container flex flex-col space-y-8 pb-12 pt-4"
    >
      <PostSkeleton v-for="i in 3" :key="`skeleton-post-${i}`" />
    </div>
    <pre v-else-if="postListError">Error: {{ postListError }}</pre>
    <div v-else class="container flex flex-col space-y-8 pb-12 pt-4">
      <Post
        v-for="post in postList"
        :key="post.id"
        :post="post"
        :is-active="isPostActive(post.id || '')"
      />
    </div>
    <CommentsDialog
      v-if="useFeatureComments"
      v-model="commentStore.isCommentsDialogOpen"
      :comments="(commentStore.comments as CommentInterface[]) || []"
      :id="commentStore.contentId"
      :title="commentStore.contentTitle"
    />
  </div>
</template>

<script setup lang="ts">
import { watch, computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useFanStore } from '@stores/fan.store';
import { useCommentStore } from '@stores/comment.store';
import { usePostStore } from '@stores/post.store';
import { useViewportVisibility } from '@/composables/useViewportVisibility';
import { useTelemetryFetcher } from '@api/tracking.api';
import { featureFlags } from '@lib/feature.flags';

import type { CommentInterface } from '@modules/Comment/comment.interface';

import Post from '@/components/modules/Post/Post.vue';
import PostSkeleton from '@modules/Post/PostSkeleton.vue';
import CommentsDialog from '@modules/Comment/CommentsDialog.vue';

// Composables
const postTelemetry = useTelemetryFetcher();

// Stores
const commentStore = useCommentStore();
const postStore = usePostStore();
const fanStore = useFanStore();

// Refs
const { postList, isPostListFetching, postListError } = storeToRefs(postStore);
const { fanId, isFanFetching } = storeToRefs(fanStore);

// Computed
const useFeatureComments = computed(() => featureFlags.comments);

// Setup viewport visibility tracking
const postRefs = ref<HTMLElement[]>([]);
const { activeElement } = useViewportVisibility(postRefs);

const isPostActive = (postId: string) => {
  const activePost = activeElement.value;
  return activePost?.dataset?.postId === postId;
};

// Update post refs when post list changes
watch(
  () => postList.value,
  () => {
    // Wait for next tick to ensure elements are mounted
    setTimeout(() => {
      const elements = document.querySelectorAll('[data-post-id]');
      postRefs.value = Array.from(elements) as HTMLElement[];
    }, 100);
  },
  { immediate: true },
);

watch(
  fanId,
  () => {
    if (fanId.value) {
      postStore.fetchPostList(undefined, 'true');
      postTelemetry.execute({
        metric: 'view-feed',
        resource: window.location.href,
      });
    }
  },
  { immediate: true },
);
</script>
